import { useMemo } from 'react';

import { foldPayers } from '../fold';
import type { EnrollmentRow, FoldResult } from '../types';

import { useAdminPayerConnectionsLiveQuery, usePayerConnectionsLiveQuery } from './api';

export type PayerConnectionsStatus = 'loading' | 'error' | 'ready';

export interface PayerConnectionsLiveResult {
  status: PayerConnectionsStatus;
  connected: boolean;
  rows: EnrollmentRow[];
  fold: FoldResult | null;
  checkedAt: string | null;
  refetch: () => void;
}

const NO_ROWS: EnrollmentRow[] = [];

/** Enrollment rows and per-payer receipt evidence come from the same authorized scope and read. */
export function usePayerConnectionsLive(organizationId?: string): PayerConnectionsLiveResult {
  const isAdmin = organizationId !== undefined;
  const provider = usePayerConnectionsLiveQuery(undefined, {
    skip: isAdmin,
    refetchOnMountOrArgChange: true,
  });
  const admin = useAdminPayerConnectionsLiveQuery(
    { organizationId: organizationId ?? '' },
    {
      skip: !isAdmin || !organizationId,
      refetchOnMountOrArgChange: true,
    },
  );
  const query = isAdmin ? admin : provider;
  // RTK keeps data from the previous args during an org switch. Only
  // currentData belongs to the selected clinic, including its receipt evidence.
  const data = query.currentData;
  const rows = data?.enrollments ?? NO_ROWS;
  const fold = useMemo(() => (data ? foldPayers(rows) : null), [rows, data]);
  const status: PayerConnectionsStatus = query.isError
    ? 'error'
    : query.isFetching || !data
      ? 'loading'
      : 'ready';

  return {
    status,
    connected: data?.connected ?? false,
    rows,
    fold,
    checkedAt: data?.checked_at ?? null,
    refetch: () => {
      void query.refetch();
    },
  };
}
